import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Building2, Monitor, DollarSign, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MetricCard, AlertsPanel, PlatformHeader } from '@/components/platform';

interface DashboardData {
  tenants: {
    total: number;
    active: number;
    suspended: number;
    trial: number;
  };
  machines: {
    total: number;
    online: number;
    offline: number;
  };
  revenue: {
    mtd: number;
    growth: number;
  };
  alerts: Array<{
    severity: 'info' | 'warning' | 'critical';
    message: string;
  }>;
}

export default function PlatformDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadDashboard() {
      try {
        setLoading(true);
        const res = await apiFetch('/api/v1/platform/dashboard');
        if (!res.ok) {
          if (res.status === 403) {
            setError('Access denied. Platform admin role required.');
            return;
          }
          throw new Error('Failed to load dashboard');
        }
        const dashboardData = await res.json();
        setData(dashboardData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }
    loadDashboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PlatformHeader />
        <div className="flex items-center justify-center p-8">
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PlatformHeader />
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <p className="text-red-600">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PlatformHeader />
      <div className="space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Platform Dashboard</h1>
            <p className="text-muted-foreground">Cross-tenant overview and management</p>
          </div>
          <Button asChild>
            <Link to="/platform/tenants/new">Create Tenant</Link>
          </Button>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Tenants"
            value={data?.tenants.total || 0}
            subtitle={`${data?.tenants.active || 0} active`}
            icon={<Building2 className="h-6 w-6" />}
          />
          <MetricCard
            title="Active Tenants"
            value={data?.tenants.active || 0}
            subtitle={`${data?.tenants.trial || 0} on trial`}
            icon={<Users className="h-6 w-6" />}
          />
          <MetricCard
            title="Total Machines"
            value={data?.machines.total || 0}
            subtitle={`${data?.machines.online || 0} online`}
            icon={<Monitor className="h-6 w-6" />}
          />
          <MetricCard
            title="Revenue (MTD)"
            value={`£${(data?.revenue.mtd || 0).toLocaleString()}`}
            trend={data?.revenue.growth ? { value: data.revenue.growth, isPositive: data.revenue.growth > 0 } : undefined}
            icon={<DollarSign className="h-6 w-6" />}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Tenant Summary</CardTitle>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/platform/tenants">View All</Link>
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                      <span className="text-sm font-medium">Active</span>
                    </div>
                    <Badge variant="secondary">{data?.tenants.active || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-blue-500" />
                      <span className="text-sm font-medium">Trial</span>
                    </div>
                    <Badge variant="secondary">{data?.tenants.trial || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="text-sm font-medium">Suspended</span>
                    </div>
                    <Badge variant="secondary">{data?.tenants.suspended || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <AlertsPanel alerts={data?.alerts || []} />
          </div>
        </div>
      </div>
    </div>
  );
}
